import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useParams } from "react-router-dom";
import { confirmDiscardIfDirty } from "../lib/unsavedGuard";
import { type Client, getClient } from "../lib/api";
import { ThemeToggle } from "../components/ThemeToggle";

/**
 * Single source of truth for the client workspace tabs.
 *
 * `path` is the URL segment and should track the tab `label` so the address
 * bar reads like the tab name (e.g. label "Gap Analysis" → path "gap-analysis").
 * "Google Business Profile" uses the short "gbp" by design. When you rename a
 * tab, update its `path` here too — App.tsx builds its <Route>s from this array,
 * so routing and the nav stay in sync automatically.
 *
 * NOTE: these are FRONTEND page paths only. Backend API endpoints
 * (/api/clients/:id/research, /supp-planner, /geo-planner, etc.) are a separate
 * contract and intentionally unchanged.
 */
export const TABS: {
  path: string;
  label: string;
  icon: string;
  /** fix665: optional inline SVG icon. When set, it renders instead of the
   *  emoji `icon` (used for the Google Business Profile tab's multi-color G). */
  iconSvg?: "google";
  component: "settings" | "gbp" | "crawl" | "audit" | "gap" | "topical" | "geo" | "content" | "internal" | "sitemaps";
  /** chunk3z-fix563: hidden from the nav bar but still routed, so deep links
   *  and the underlying tab keep working. Re-enable by removing the flag.
   *  TODO(Eddie): un-hide Topical Relevance, Geo Relevance, and Internal Hubs
   *  when you're ready to work on those sections. */
  hidden?: boolean;
}[] = [
  { path: "settings", label: "Client Settings", icon: "⚙", component: "settings" },
  // fix667: label shortened back to "GBP" to keep the nav compact, but the
  // Google G icon (iconSvg) stays instead of the old pin.
  { path: "gbp", label: "GBP", icon: "📍", iconSvg: "google", component: "gbp" },
  { path: "crawl", label: "Crawl", icon: "🌐", component: "crawl" },
  { path: "gap-analysis", label: "Gap Analysis", icon: "🔍", component: "gap" },
  { path: "content-audit", label: "Content Audit", icon: "✅", component: "audit" },
  { path: "topical-relevance", label: "Topical Relevance", icon: "📌", component: "topical", hidden: true },
  { path: "geo-relevance", label: "Geo Relevance", icon: "📊", component: "geo" },
  { path: "content-generation", label: "Content Generation", icon: "✨", component: "content" },
  { path: "internal-hubs", label: "Internal Hubs", icon: "🔗", component: "internal", hidden: true },
  // chunk3z-fix387: site migration — Wayback history scan + sitemap tree, then
  // (next drop) redirect mapping + missing-pages report.
  { path: "sitemaps", label: "Sitemaps", icon: "🗺", component: "sitemaps" },
];

/** The tab a bare /clients/:id should land on. */
export const DEFAULT_TAB_PATH = "settings";

export type WorkspaceContext = {
  client: Client;
  refreshClient: () => Promise<void>;
};

export default function ClientWorkspacePage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  // fix312: show the Back link in the sticky nav ONLY once the top bar (which
  // has its own Back link) has scrolled out of view — so there aren't two Back
  // links visible at once when you're at the top.
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHidden, setHeaderHidden] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderHidden(!entry!.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [client]);

  async function refreshClient() {
    if (!clientId) return;
    const data = await getClient(Number.parseInt(clientId, 10));
    setClient(data);
  }

  useEffect(() => {
    if (!clientId) return;
    getClient(Number.parseInt(clientId, 10))
      .then(setClient)
      .catch((err) => setError(err.message));
  }, [clientId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger">{error}</p>
          <Link to="/" className="text-accent text-sm mt-3 inline-block">
            ← Back to clients
          </Link>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted text-sm">
        Loading client...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-subtle">
      {/* Top bar — scrolls away normally. Only the tab nav below is sticky. */}
      <header ref={headerRef} className="bg-bg-card">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={(e) => {
                if (!confirmDiscardIfDirty()) e.preventDefault();
              }}
              className="text-text-muted hover:text-text text-sm"
            >
              ← Back
            </Link>
            <div className="w-7 h-7 bg-accent rounded flex items-center justify-center text-white text-sm font-semibold">
              {client.businessName.charAt(0).toUpperCase()}
            </div>
            <h1 className="text-text font-semibold">{client.businessName}</h1>
            {normalizeWebsiteUrl(client.websiteUrl) && (
              <a
                href={normalizeWebsiteUrl(client.websiteUrl)!}
                target="_blank"
                rel="noreferrer"
                aria-label="Open client website in new tab"
                className="text-accent hover:text-accent-hover transition-colors -ml-0.5 inline-flex items-center"
              >
                <ExternalLinkIcon />
              </a>
            )}
          </div>
          {/* Right side: theme toggle. Available on every client workspace
              tab (Crawl/GBP/Research/Supp/Geo/Generate/Settings) for parity
              with the standalone Settings page. Intentionally NOT on the
              Client List page (per-spec). */}
          <ThemeToggle />
        </div>
      </header>

      {/* Tab bar — moved OUT of <header> so its sticky containing block is the
          full-height page div, not the short header box (a sticky element can
          only stick within its parent's bounds; trapped in the header it had
          no room and scrolled away). Now it pins to the top of the viewport
          while the top bar above scrolls off. */}
      <nav className="sticky top-0 z-30 bg-bg-card border-b border-border">
        {/* fix316/fix317: centered at max-w-6xl to line up with everything else.
            The Back link is absolutely positioned in the LEFT GUTTER (outside the
            tab row's flow) so it takes zero layout width AND doesn't require
            padding the row — padding shifted every tab right and clipped
            "Internal Hubs". Tabs stay exactly where they are with or without
            Back; the row scrolls horizontally only if it ever overflows. */}
        <div className="max-w-6xl mx-auto px-4 relative">
          {headerHidden && (
            <Link
              to="/"
              onClick={(e) => {
                if (!confirmDiscardIfDirty()) e.preventDefault();
              }}
              className="hidden lg:flex items-center absolute right-full mr-2 top-1/2 -translate-y-1/2 z-10 text-text-muted hover:text-text text-[13px] whitespace-nowrap"
              title="Back to clients"
            >
              ← Back
            </Link>
          )}
          {/* fix564: with 3 tabs hidden (fix563) the remaining tabs bunched to
              the left. justify-between spreads them across the full max-w-6xl
              width so the bar reads the same width as before. Tabs stay shrink-0
              so on a narrow viewport the row still scrolls horizontally instead
              of squashing. */}
          <div className="flex items-stretch overflow-x-auto no-scrollbar">
            {TABS.filter((tab) => !tab.hidden).map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                onClick={(e) => {
                  // fix260: if the current tab (e.g. Client Settings) has unsaved
                  // changes, confirm before navigating away — leaving would
                  // unmount it and lose the edits.
                  if (!confirmDiscardIfDirty()) {
                    e.preventDefault();
                  }
                }}
                className={({ isActive }) =>
                  // fix660: flex-1 so every tab takes an equal share of the nav
                  // width — evenly spaced across the same total width regardless
                  // of how many tabs are shown or how long each label is.
                  `flex-1 text-center text-[13px] px-2 py-2.5 border-b-2 transition whitespace-nowrap ${
                    isActive
                      ? "text-accent border-accent"
                      : "text-text-muted border-transparent hover:text-text"
                  }`
                }
              >
                <span className="mr-1 opacity-80">
                  {tab.iconSvg === "google" ? <GoogleGIcon /> : tab.icon}
                </span>
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Tab content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Outlet context={{ client, refreshClient } satisfies WorkspaceContext} />
      </main>
    </div>
  );
}

export function ClientWorkspaceIndexRedirect() {
  return <Navigate to={DEFAULT_TAB_PATH} replace />;
}

/**
 * fix665: inline multi-color Google "G" mark for the Google Business Profile
 * nav tab. Sized to match the emoji icons on the other tabs. Colors are the
 * official Google brand palette; rendered inline so it needs no asset import.
 */
function GoogleGIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block align-[-1px]"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

export function ComingSoonTab({ label }: { label: string }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-12 text-center shadow-card">
      <h2 className="text-text font-semibold mb-1">{label}</h2>
      <p className="text-text-muted text-sm">This tab will be available in the next update.</p>
    </div>
  );
}

/**
 * Inline "open in new tab" SVG icon. Sized to sit cleanly next to the
 * client name heading. Uses currentColor so the parent's text color
 * controls the fill — the link wrapper sets it to text-accent.
 */
function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 4h6v6" />
      <path d="M10 14L20 4" />
      <path d="M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </svg>
  );
}

/**
 * Normalize a freeform website URL string for use in an href.
 *
 *  - Trim whitespace
 *  - Empty or whitespace-only → null (caller hides the icon)
 *  - Missing scheme ("hamblinfamilydentistry.com") → prepend "https://"
 *  - Already has a scheme → returned as-is
 *
 * This is defensive — backend validation enforces the field is non-empty
 * and parses, but legacy rows from before that enforcement might still
 * be a bare hostname.
 */
function normalizeWebsiteUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}
